import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { View, Text, TouchableOpacity, ScrollView, Animated, Modal, TextInput, useWindowDimensions, Platform } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PlatformIcon, TimelineItemRow, FullscreenPoiModal, EditorHeader, ShareButton, FullscreenWrapper, TravelDashboardWidget } from '@cubemeet/ui';
// 临时直接引用 Web 卡片列表页作为选择器（仅为尽快接入可视化，后续可抽到共享包）
import { CardList } from '@cubemeet/ui';
import type { CardType } from '@cubemeet/ui';
import { useItineraryEditor, genUniqueId } from './useItineraryEditor';
import { useTravelPostPreviewLauncher } from './publish/hooks/useTravelPostPreviewLauncher';
import { resolveTravelBackgroundSourceLoose, resolveTravelBackgroundSource } from '@cubemeet/ui';
// removed small edit modal icons (editing now via Fullscreen POI modal)
import { extractIds, generateNavigationPath } from '@cubemeet/core/utils/id-validator';
import { checkIsUserLiked, likeUser, unlikeUser } from '@cubemeet/core/services/userProfileService';

import { useGlobalToast } from '../../../../apps/web/src/contexts/ToastContext';
import TeamEditToggle from './components/TeamEditToggle';
import FloatingActionButton from './components/FloatingActionButton';
import SaveFab from './components/SaveFab';
import { QuickAddMenu } from '@cubemeet/ui';
import NearbyMap, { NearbySpot } from '@cubemeet/ui/components/travel/NearbyMap';
import {
  MdPlace,
  MdRestaurant,
  MdEvent,
  MdHotel,
  MdDirectionsTransit,
  MdMoreHoriz,
  MdOndemandVideo,
  MdAttachFile,
  MdShoppingCart,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdCode,
} from 'react-icons/md';
import FullscreenCustomEditorModal from './components/FullscreenCustomEditorModal';
import CardRenderer from './components/CardRenderer';
import TabStrip from './components/TabStrip';
import ItineraryTabs from './components/ItineraryTabs';
import AddItemForm from './components/AddItemForm';
import CardFormHost from '@cubemeet/ui/components/cards/CardFormHost';
import TimelineItem from './components/TimelineItem';
import TimelineList from './components/TimelineList';
import PoiModalWrapper from './components/PoiModalWrapper';
import { createEmptyMealPayload } from './utils/emptyPayloads/meal';
import { createEmptyPoiPayload } from './utils/emptyPayloads/poi';
import { createEmptyLodgingPayload } from './utils/emptyPayloads/lodging';
import { createEmptyActivityPayload } from './utils/emptyPayloads/activity';
import { createEmptyShoppingPayload } from './utils/emptyPayloads/shopping';
import styles from './components/ItineraryEditor.styles';
import { formatDateShort, parseTimeToMinutes, getNowMinutes } from './timeUtils';
import useFabBottomOffset from './hooks/useFabBottomOffset';
import WebIcon from './components/WebIcon';
import { TimeWheelPicker } from '@cubemeet/ui';
import { ORANGE } from './components/itineraryUtils';
import EditItemModal from './components/EditItemModal';
import { useRouteMap } from './hooks/useRouteMap';
import RouteMapSheet from './components/RouteMapSheet';
import PublishGuideButton from './publish/components/PublishGuideButton';
import ItinerarySettingsModal from './components/ItinerarySettingsModal';
import ItineraryMoreMenu from './components/ItineraryMoreMenu';

export interface ItineraryEditorProps {
  initialItinerary?: any;
  initialFormData?: any;
  expectedDays?: number;
  initialSelectedDayIndex?: number;
  onDaySelect?: (dayIndex: number) => void;
  onSave?: (result: { normalized: any; text: string }) => void;
  onBack?: () => void;
  onEdit?: () => void;
  showLocationBadge?: boolean;
  supabaseClient?: any;
  currentUserId?: string;
  onToggleTeamEdit?: (allow: boolean) => void;
  // ShareButton 相关 props
  contentType?: 'card' | 'trip';  // 内容类型（用于分享）
  contentId?: string;              // 内容 ID（cardId 或 groupId）
  creator?: any;                   // 创建人信息 { id, display_name, avatar_url, bio }
  showShareButton?: boolean;       // 是否显示分享按钮
  initialShowRouteMap?: boolean;   // 是否初始显示路线地图
}

const ItineraryEditor: React.FC<ItineraryEditorProps> = ({ initialItinerary, initialFormData, expectedDays, initialSelectedDayIndex = 0, onDaySelect, onSave, onBack, onEdit, showLocationBadge = true, supabaseClient, currentUserId: propCurrentUserId, onToggleTeamEdit, contentType, contentId, creator, showShareButton = false, initialShowRouteMap = false }) => {
  const editor = useItineraryEditor({ initialItinerary, initialFormData, expectedDays });
  const launchPreview = useTravelPostPreviewLauncher();
  
  // 確保頁面載入時滾動到頂部
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);
  
  // 让 NoteEditor 能直接访问 editor 做立即更新（只在 web 环境有效）
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__TIMELINE_EDITOR__ = editor;
      return () => {
        delete (window as any).__TIMELINE_EDITOR__;
      };
    }
  }, [editor]);

  // router navigation (used by ShareButton handlers etc.)
  const navigate = useNavigate();
  // Restore editor hash after saves that cause a full-page reload.
  // Strategy: before saving we persist current location.hash to sessionStorage.
  // On mount (or after save), if a pending hash is present we replace the URL to restore the editor view.
  React.useEffect(() => {
    try {
      if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
      const pending = sessionStorage.getItem('cm:editorHashBeforeSave');
      if (pending) {
        // If current location doesn't already include the pending hash, restore it without reloading
        if (window.location.hash !== pending) {
          const newUrl = window.location.pathname + pending + window.location.search;
          try { history.replaceState?.(null, '', newUrl); } catch (e) { window.location.hash = pending; }
        }
        try { sessionStorage.removeItem('cm:editorHashBeforeSave'); } catch (e) {}
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // helper: collect owner-like candidate fields from arbitrary input objects
  const collectOwnerCandidates = (obj: any) => {
    return obj ? {
      owner: obj.owner,
      creator: obj.creator,
      user_id: obj.user_id,
    } : {};
  };

  const _ownerCandidates = collectOwnerCandidates(initialItinerary);
  const _formCandidates = collectOwnerCandidates(initialFormData);

  let ownerId: string | undefined = undefined;

  const pickFrom = (obj: any) => {
    if (!obj) return undefined;
    return obj.ownerId || obj.owner_id || obj.creator_id || obj.creatorId || obj.created_by || obj.owner || obj.user_id;
  };

  // prefer initialItinerary but fallback to initialFormData (e.g., currentTravel.creator_id)
  ownerId = pickFrom(initialItinerary) || pickFrom(initialFormData);
  // nested creator object fallback
  if (!ownerId) {
    if (initialItinerary && initialItinerary.creator && typeof initialItinerary.creator === 'object') {
      ownerId = initialItinerary.creator.id || initialItinerary.creator.userId || initialItinerary.creator._id;
    }
    if (!ownerId && initialFormData && initialFormData.creator && typeof initialFormData.creator === 'object') {
      ownerId = initialFormData.creator.id || initialFormData.creator.userId || initialFormData.creator._id;
    }
  }
  const initialAllowTeamEdit = (initialItinerary && (initialItinerary.allowTeamEdit ?? initialItinerary.allow_team_edit ?? initialItinerary.teamEdit)) ?? false;
  // Determine group id (used for backend persistence and local cache key)
  const groupIdForLocal = (initialFormData && (initialFormData.id || initialFormData.groupId)) || (initialItinerary && (initialItinerary.id || initialItinerary.groupId));
  const localStorageKey = groupIdForLocal ? `cm:group:${groupIdForLocal}:allowTeamEdit` : null;
  // Read cached value from localStorage (if available) so toggle survives backend failures / offline
  let cachedAllow: boolean | undefined = undefined;
  if (typeof window !== 'undefined' && localStorageKey) {
    try {
      const v = window.localStorage.getItem(localStorageKey);
      if (v !== null) cachedAllow = (v === '1' || v === 'true');
    } catch (e) {
      // ignore
    }
  }
  // Prefer the server-provided value (initialAllowTeamEdit) as authoritative.
  // Use cached localStorage value only as a fallback when server value is not available.
  const [allowTeamEdit, setAllowTeamEdit] = useState<boolean>(initialAllowTeamEdit ?? (cachedAllow ?? false));

  // FAB offset to keep it above sticky bottom nav on web (moved to hook)
  const fabBottomOffset = useFabBottomOffset();

  // responsive helpers (useWindowDimensions from react-native)
  const windowDims = useWindowDimensions && useWindowDimensions();
  const _width = (windowDims && (windowDims.width ?? windowDims.window?.width)) || (typeof window !== 'undefined' ? window.innerWidth : 1024);
  const isNarrow = Number(_width) < 900;
  const isDesktop = Number(_width) >= 1024;
  const isTablet = Number(_width) >= 720 && Number(_width) < 1024;

  // permissions: prefer prop, fallback to redux auth state
  const reduxUser = useSelector((s: any) => s?.auth?.user);
  const currentUserId = propCurrentUserId ?? reduxUser?.id;
  // track whether current user appears in group_members table for this group (fallback)
  const [isGroupMember, setIsGroupMember] = useState<boolean>(false);
  const collaborators = initialItinerary?.collaborators || initialItinerary?.members || [];
  const isOwner = Boolean(currentUserId && ownerId && String(currentUserId) === String(ownerId));
  const isCollaborator = Boolean(currentUserId && (
    (collaborators && Array.isArray(collaborators) && collaborators.findIndex((c: any) => (c === currentUserId || c.userId === currentUserId || c.id === currentUserId)) !== -1)
    || isGroupMember
  ));
  const canEdit = isOwner || (allowTeamEdit && isCollaborator);
  const canDelete = isOwner;

  // Auto-enable edit mode for editors - REMOVED to default to preview mode
  // useEffect(() => {
  //   if (canEdit) {
  //     setIsInEditMode(true);
  //   }
  // }, [canEdit]);

  const memberProfiles = useMemo(() => {
    const map: Record<string, { display_name?: string | null; avatar_url?: string | null }> = {};
    const source: any = initialFormData || {};

    const register = (userId?: string | null, name?: string | null, avatar?: string | null) => {
      if (!userId) return;
      map[String(userId)] = {
        display_name: name ?? null,
        avatar_url: avatar ?? null,
      };
    };

    const members = Array.isArray(source?.members) ? source.members : [];
    members.forEach((member: any) => {
      const userId = member?.user_id || member?.userId || member?.id;
      const profile = member?.user_profile || member?.profile || null;
      const displayName = (profile && (profile.display_name || profile.nickname || profile.name))
        || member?.display_name
        || member?.nickname
        || member?.name
        || null;
      const avatarUrl = (profile && (profile.avatar_url || profile.avatarUrl))
        || member?.avatar_url
        || member?.avatarUrl
        || null;
      register(userId, displayName, avatarUrl);
    });

    const creatorId = source?.creator_id || source?.creatorId || source?.owner_id || source?.ownerId;
    if (creatorId) {
      const creatorProfile = source?.creator || null;
      const displayName = (creatorProfile && (creatorProfile.display_name || creatorProfile.nickname || creatorProfile.name))
        || source?.creator_name
        || null;
      const avatarUrl = (creatorProfile && (creatorProfile.avatar_url || creatorProfile.avatarUrl)) || null;
      if (!map[String(creatorId)]) {
        register(creatorId, displayName, avatarUrl);
      }
    }

    if (reduxUser?.id) {
      const meta = reduxUser?.user_metadata || {};
      const displayName = reduxUser.display_name
        || reduxUser.full_name
        || reduxUser.name
        || meta.full_name
        || meta.display_name
        || reduxUser.email
        || null;
      const avatarUrl = reduxUser.avatar_url
        || reduxUser.avatarUrl
        || meta.avatar_url
        || meta.avatarUrl
        || null;
      register(reduxUser.id, displayName, avatarUrl);
    }

    if (creator && creator.id) {
      register(creator.id, creator.display_name || null, creator.avatar_url || null);
    }

    return map;
  }, [initialFormData, reduxUser, creator]);

  // 自定义图标渲染函数，支持 PlatformIcon 作为 fallback
  const renderCustomIcon = React.useCallback((key: string, iconName: string) => {
    switch (key) {
      case 'poi': return <MdPlace style={{ color: '#fff', fontSize: 18 }} />;
      case 'meal': return <MdRestaurant style={{ color: '#fff', fontSize: 18 }} />;
      case 'activity': return <MdEvent style={{ color: '#fff', fontSize: 18 }} />;
      case 'lodging': return <MdHotel style={{ color: '#fff', fontSize: 18 }} />;
      case 'transport': return <MdDirectionsTransit style={{ color: '#fff', fontSize: 18 }} />;
      case 'shopping': return <MdShoppingCart style={{ color: '#fff', fontSize: 18 }} />;
      case 'file': return <MdAttachFile style={{ color: '#fff', fontSize: 18 }} />;
      case 'video': return <MdCode style={{ color: '#fff', fontSize: 18 }} />;
      case 'other': return <MdMoreHoriz style={{ color: '#fff', fontSize: 18 }} />;
      default: return <PlatformIcon name={iconName} size={18} color="#fff" />;
    }
  }, []);

  // global toast (call hook at top-level of component)
  const { showSuccess, showError, showInfo, showWarning } = useGlobalToast();
  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [isCreatorLiked, setIsCreatorLiked] = useState<boolean>(false);
  // local UI edit mode toggle (controls whether timeline is editable)
  // Default to true if user has edit permissions (will be synced in useEffect)
  const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [showMoreMenu, setShowMoreMenu] = useState<boolean>(false);

  // UI state: track local dirty/saving state and various editor sub-views
  const [dirty, setDirty] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [quickAddOpen, setQuickAddOpen] = useState<boolean>(false);
  // When closing QuickAddMenu the component runs an exit animation before
  // unmounting; to avoid the FAB popping back into view while the menu is
  // still animating out we keep a short `quickAddClosing` flag that delays
  // showing the FAB until the exit animation has finished.
  const [quickAddClosing, setQuickAddClosing] = useState<boolean>(false);
  const quickAddClosingTimeout = useRef<any>(null);
  const prevQuickAddOpenRef = useRef<boolean>(quickAddOpen);

  useEffect(() => {
    try {
      // Transition: open -> closed
      if (prevQuickAddOpenRef.current && !quickAddOpen) {
        setQuickAddClosing(true);
        if (quickAddClosingTimeout.current) {
          window.clearTimeout(quickAddClosingTimeout.current);
          quickAddClosingTimeout.current = null;
        }
        // Match QuickAddMenu exit timing (approx 200-220ms). Add small buffer.
        quickAddClosingTimeout.current = window.setTimeout(() => {
          setQuickAddClosing(false);
          quickAddClosingTimeout.current = null;
        }, 260);
      }
      // Transition: closed -> open
      if (!prevQuickAddOpenRef.current && quickAddOpen) {
        if (quickAddClosingTimeout.current) {
          window.clearTimeout(quickAddClosingTimeout.current);
          quickAddClosingTimeout.current = null;
        }
        setQuickAddClosing(false);
      }
    } catch (e) {}
    prevQuickAddOpenRef.current = quickAddOpen;
    return () => {
      if (quickAddClosingTimeout.current) {
        window.clearTimeout(quickAddClosingTimeout.current);
        quickAddClosingTimeout.current = null;
      }
    };
  }, [quickAddOpen]);
  const [currentSubView, setCurrentSubView] = useState<'timeline' | 'add-item' | 'select-card'>('timeline');
  // 指令：让时间轴滚动到指定 index（用于新增占位后自动定位）
  const [timelineScrollToIndex, setTimelineScrollToIndex] = useState<number | null>(null);
  // expanded cards set (tracks which card ids are expanded in timeline)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  // NOTE: per new UX decision, inline/pencil edit modal removed; editing happens in Fullscreen POI modal
  // POI modal state
  const [openPoi, setOpenPoi] = useState<any>({ visible: false });
  // custom editor modal state
  const [openCustomEditor, setOpenCustomEditor] = useState<boolean>(false);
  // inline edit state (time/content)
  const [inlineEditTarget, setInlineEditTarget] = useState<{ dayIndex: number; itemIndex: number; mode?: string } | null>(null);
  const [inlineEditTime, setInlineEditTime] = useState<string | undefined>(undefined);
  // card picker flow state
  const [createForm, setCreateForm] = useState<any>({ time: '09:00' });
  const [pendingCard, setPendingCard] = useState<any>(null);
  const [timePickerVisible, setTimePickerVisible] = useState<boolean>(false);
  const [placeholderCardInfo, setPlaceholderCardInfo] = useState<any>(null);
  // image lightbox state
  const [lightboxState, setLightboxState] = useState<{ images: any[]; currentIndex: number } | null>(null);
  // When lightbox opens, hide the app bottom nav portal so the lightbox appears truly fullscreen
  const _bottomNavPortalPrevDisplay = React.useRef<string | null>(null);
  const _bottomNavRootPrevDisplays = React.useRef<Array<{ el: Element; prev: string | null }>>([]);
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // when lightboxState becomes falsy -> restore saved displays
    if (!lightboxState) {
      try {
        const portal = document.getElementById('app-bottom-nav-portal');
        if (portal) {
          const prev = _bottomNavPortalPrevDisplay.current;
          if (prev !== null && prev !== '') {
            try { portal.style.setProperty('display', prev); } catch (e) {}
          } else {
            try { portal.style.removeProperty('display'); } catch (e) {}
          }
        }
        _bottomNavPortalPrevDisplay.current = null;

        const roots = document.getElementsByClassName('app-bottom-nav-root');
        for (let i = 0; i < roots.length; i++) {
          const el = roots[i];
          const rec = _bottomNavRootPrevDisplays.current.find(r => r.el === el);
          if (rec) {
            if (rec.prev !== null && rec.prev !== '') {
              try { (el as HTMLElement).style.setProperty('display', rec.prev); } catch (e) {}
            } else {
              try { (el as HTMLElement).style.removeProperty('display'); } catch (e) {}
            }
          }
        }
        _bottomNavRootPrevDisplays.current = [];
      } catch (e) {
        // ignore
      }
      return;
    }

    // when lightboxState is truthy -> hide portals (saving previous inline styles)
    try {
      const portal = document.getElementById('app-bottom-nav-portal');
      if (portal) {
        // save current inline display (may be empty string)
        _bottomNavPortalPrevDisplay.current = portal.style.getPropertyValue('display');
        portal.style.setProperty('display', 'none', 'important');
      }

      const roots = document.getElementsByClassName('app-bottom-nav-root');
      _bottomNavRootPrevDisplays.current = [];
      for (let i = 0; i < roots.length; i++) {
        const el = roots[i];
        const prev = (el as HTMLElement).style.getPropertyValue('display');
        _bottomNavRootPrevDisplays.current.push({ el, prev });
        try { (el as HTMLElement).style.setProperty('display', 'none', 'important'); } catch (e) {}
      }
    } catch (e) {
      // ignore
    }
  }, [lightboxState]);
  // sheet expansion state (card-picker and route-map)
  const [sheetExpanded, setSheetExpanded] = useState<boolean>(false);
  // route map state
  const [showRouteMap, setShowRouteMap] = useState<boolean>(initialShowRouteMap);
  // Sheet drag state for smooth drawer behavior
  const [sheetHeight, setSheetHeight] = useState<number>(70); // percentage of viewport height
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const dragStartY = useRef<number>(0);
  const dragStartHeight = useRef<number>(70);
  
  // Helper function to close route map and ensure body scroll is restored
  const closeRouteMap = React.useCallback(() => {
    setShowRouteMap(false);
    setSheetHeight(70);
    // Force clear body styles in case nested modals corrupted the state
    if (typeof document !== 'undefined') {
      const body = document.body as HTMLBodyElement;
      // Only clear if we're in a locked state
      if (body.style.position === 'fixed') {
        const scrollY = Math.abs(parseInt(body.style.top || '0', 10));
        body.style.overflow = '';
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        try { window.scrollTo(0, scrollY); } catch {}
      }
    }
    // Clear the lock ref
    bodyLockRef.current = null;
  }, []);
  
  // lock body scroll when bottom-sheet (select-card or route-map) is open
  const bodyLockRef = React.useRef<{ y: number; overflow: string; position: string; top: string; width: string } | null>(null);
  const cardPickerScrollRef = React.useRef<HTMLDivElement | null>(null);
  const scrollCardPickerToTop = React.useCallback((_: number, meta?: { recommendedScrollTop?: number }) => {
    const node = cardPickerScrollRef.current;
    if (!node) return;
    const target = typeof meta?.recommendedScrollTop === 'number' ? Math.max(meta.recommendedScrollTop, 0) : 0;
    try {
      node.scrollTo({ top: target, behavior: 'smooth' });
    } catch (err) {
      try { node.scrollTop = target; } catch {}
    }
  }, []);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const body = document.body as HTMLBodyElement;
    const shouldLock = currentSubView === 'select-card' || showRouteMap;
    
    if (shouldLock) {
      // only lock if not already locked
      if (!bodyLockRef.current) {
        bodyLockRef.current = {
          y: window.scrollY || window.pageYOffset || 0,
          overflow: body.style.overflow,
          position: body.style.position,
          top: body.style.top,
          width: body.style.width,
        };
        const y = bodyLockRef.current.y;
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${y}px`;
        body.style.width = '100%';
      }
    } else {
      // unlock if currently locked
      if (bodyLockRef.current) {
        const prev = bodyLockRef.current;
        body.style.overflow = prev.overflow || '';
        body.style.position = prev.position || '';
        body.style.top = prev.top || '';
        body.style.width = prev.width || '';
        try { window.scrollTo(0, prev.y || 0); } catch {}
        bodyLockRef.current = null;
      }
    }
    return () => {
      // cleanup on unmount if still locked
      if (bodyLockRef.current && typeof document !== 'undefined') {
        const prev = bodyLockRef.current;
        const body = document.body as HTMLBodyElement;
        body.style.overflow = prev.overflow || '';
        body.style.position = prev.position || '';
        body.style.top = prev.top || '';
        body.style.width = prev.width || '';
        try { window.scrollTo(0, prev.y || 0); } catch {}
        bodyLockRef.current = null;
      }
    };
  }, [currentSubView, showRouteMap]);

  // Card add flow state (pending lightweight/full card and a small create form)
  // Sheet for "今日路线" (moved into the main return). Previously a misplaced JSX block was here and caused a parse error; the sheet will be re-inserted inside the returned JSX so it's rendered correctly.
    const saveItinerary = async () => {
      // permission check: disallow saving when user cannot edit
      if (!canEdit) {
        try { showError && showError('您沒有權限保存該行程'); } catch (e) {}
        return;
      }
    
      if (!dirty) return;
    setSaving(true);
    try {
      // Prefer delegating persistence to parent via onSave prop if provided.
      // The parent (e.g., TravelModule) knows the correct table/schema to persist to.
      // Persist current hash to sessionStorage so we can restore editor view if parent causes a full reload/navigation.
      try { if (typeof sessionStorage !== 'undefined' && typeof window !== 'undefined') sessionStorage.setItem('cm:editorHashBeforeSave', window.location.hash || ''); } catch (e) {}
      if (typeof onSave === 'function') {
        // Optimistic local feedback: mark not dirty and return immediately to keep UI responsive.
        // Perform the actual persistence in background so the Save button doesn't block or force a full-page wait.
        setDirty(false);
        try { showSuccess && showSuccess('行程已保存（正在同步）'); } catch (e) {}

        (async () => {
          const started = Date.now();
          try {
            await onSave({ normalized: editor.itinerary, text: JSON.stringify(editor.itinerary) });
            const took = Date.now() - started;
            console.log('[ItineraryEditor] background onSave finished in', took, 'ms');
            // Clear the pending hash if save completed without a full reload
            try { if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('cm:editorHashBeforeSave'); } catch (e) {}
          } catch (e) {
            console.error('[ItineraryEditor] background onSave failed', e);
            try { showError && showError('同步失敗，已在本機保存'); } catch (err) {}
            // keep dirty=false since local save already applied; user can try Save again if needed
          }
        })();
      } else {
        // No parent onSave provided: persist locally (localStorage) as a temporary fallback.
        try {
          const key = `cm:itinerary:group:${groupIdForLocal}`;
          try { window.localStorage.setItem(key, JSON.stringify(editor.itinerary)); } catch (e) { /* ignore */ }
          setDirty(false);
          try { showInfo && showInfo('本機已保存（未同步至伺服器）'); } catch (e) {}
        } catch (e) {
          // ignore local save errors
          throw e;
        }
      }
    } catch (e) {
      console.error('saveItinerary failed', e);
      try { showError && showError('保存失敗，請重試'); } catch (err) {}
      // keep dirty=true so user can retry
    } finally {
      setSaving(false);
    }
  };

  // animated toggle position (px offset from center)
  // NOTE: translateX positive value moves knob to the right. We want allowTeamEdit=true => knob on RIGHT.
  const toggleX = React.useRef(new Animated.Value(allowTeamEdit ? 6 : -6)).current;

  // Keep the knob position in sync when allowTeamEdit changes (e.g. updated from server or via prop)
  useEffect(() => {
    try {
      // ensure knob moves RIGHT when allowTeamEdit is true, LEFT when false
  Animated.timing(toggleX, { toValue: allowTeamEdit ? 6 : -6, duration: 200, useNativeDriver: true }).start();
    } catch (e) {}
  }, [allowTeamEdit, toggleX]);

  // Ensure we fetch canonical server value on mount so members see the real server state.
  useEffect(() => {
    const groupId = groupIdForLocal;
    if (!groupId || !supabaseClient) return;
    let mounted = true;
    (async () => {
      try {
        const res = await supabaseClient.from('groups').select('allow_team_edit').eq('id', groupId).maybeSingle();
        const data = res && (res.data ?? (res as any).data);
        const error = res && (res.error ?? (res as any).error);
        if (!mounted) return;
        if (!error && data && typeof data.allow_team_edit !== 'undefined') {
          const serverVal = Boolean(data.allow_team_edit);
          setAllowTeamEdit(serverVal);
          if (typeof window !== 'undefined' && localStorageKey) {
            try { window.localStorage.setItem(localStorageKey, serverVal ? '1' : '0'); } catch (e) {}
          }
        } else if (!error && !data) {
          // group not found; keep local state as-is
        }
      } catch (e) {
        // ignore fetch errors; keep current state
      }
    })();
    return () => { mounted = false; };
  }, [supabaseClient, groupIdForLocal, localStorageKey]);

  // If initialItinerary doesn't include members, check `group_members` table to see if current user is a member.
  useEffect(() => {
    if (!supabaseClient || !groupIdForLocal || !currentUserId) return;
    let mounted = true;
    (async () => {
      try {
        const res = await supabaseClient.from('group_members').select('user_id').eq('group_id', groupIdForLocal).eq('user_id', currentUserId).maybeSingle();
        const data = res && (res.data ?? (res as any).data);
        const error = res && (res.error ?? (res as any).error);
        if (!mounted) return;
        if (!error && data && (data as any).user_id) {
          setIsGroupMember(true);
        } else {
          setIsGroupMember(false);
        }
      } catch (e) {
        // ignore network/permission errors; leave as false
      }
    })();
    return () => { mounted = false; };
  }, [supabaseClient, groupIdForLocal, currentUserId]);
  

  // Persist allowTeamEdit to backend (optimistic)
  const persistAllowTeamEdit = async (next: boolean) => {
    // optimistic: update UI immediately and persist to localStorage so the user's choice is not lost
    setAllowTeamEdit(next);
    if (onToggleTeamEdit) onToggleTeamEdit(next);
    if (typeof window !== 'undefined' && localStorageKey) {
      try { window.localStorage.setItem(localStorageKey, next ? '1' : '0'); } catch (e) { /* ignore */ }
    }

    // determine group id from props/context
    const groupId = (initialFormData && (initialFormData.id || initialFormData.groupId)) || (initialItinerary && (initialItinerary.id || initialItinerary.groupId));
    if (!groupId || !supabaseClient) return;

    try {
      // Directly update the canonical column we just added: allow_team_edit
      const { error } = await supabaseClient.from('groups').update({ allow_team_edit: next }).eq('id', groupId);
      if (error) throw error;
      // success - nothing else to do
    } catch (e) {
      // Keep local setting (we already wrote to localStorage), but inform the user
      console.error('Persist allowTeamEdit failed', e);
      if (typeof window !== 'undefined') {
        const msg = e && (e as any).message ? String((e as any).message) : '保存團隊編輯設定失敗，請稍後重試';
        window.alert(`保存團隊編輯設定失敗，已在本機保存設定，但未同步到伺服器。\n\n${msg}`);
      }
    }
  };
  
  // Check creator like status
  useEffect(() => {
    if (!creator?.id || !supabaseClient) return;
    let mounted = true;
    (async () => {
      try {
        const { data: { user } } = await supabaseClient.auth.getUser();
        if (user && mounted) {
           const liked = await checkIsUserLiked(user.id, creator.id);
           if (mounted) setIsCreatorLiked(liked);
        }
      } catch (e) { console.warn(e); }
    })();
    return () => { mounted = false; };
  }, [creator?.id, supabaseClient]);

  const handleCreatorLikePress = async () => {
    try {
      const client = supabaseClient ?? (await import('@cubemeet/core/services/supabase')).supabase;
      const { data: { user } } = await client.auth.getUser();
      if (!user) {
        const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
        navigate(`/auth?referer=${ref}`);
        return;
      }
      
      if (!creator?.id) return;

      if (isCreatorLiked) {
        await unlikeUser(user.id, creator.id);
        setIsCreatorLiked(false);
        showSuccess && showSuccess('已取消點讚');
      } else {
        await likeUser(user.id, creator.id);
        setIsCreatorLiked(true);
        showSuccess && showSuccess('已點讚');
      }
    } catch (e: any) {
      console.error('Creator like error:', e);
      showError && showError(e.message || '操作失败');
    }
  };

  const days = editor.itinerary?.days || [];
  

  const bgSourceLoose = resolveTravelBackgroundSourceLoose(initialItinerary, initialFormData) as any;
  const bgSource = bgSourceLoose || (resolveTravelBackgroundSource(initialItinerary, initialFormData) as any);

  // helper: robust owner extraction for arbitrary card shapes
  const extractCardOwner = (card: any) => {
    if (!card) return undefined;
    // canonical field: creator_id (db schema uses creator_id)
    if (card.creator_id) return card.creator_id;
    // minimal fallback: nested creator object
    if (card.creator && (card.creator.id || card.creator.userId || card.creator._id)) return card.creator.id || card.creator.userId || card.creator._id;
    return undefined;
  };

  const isValidId = (v: any) => {
    if (v === null || typeof v === 'undefined') return false;
    const s = String(v).trim();
    if (!s) return false;
    const low = s.toLowerCase();
    if (low === 'null' || low === 'undefined' || low === '0') return false;
    return true;
  };

  

  

  const selectedDay = days[editor.selectedDayIndex] || { items: [] };
  
  // 路线地图数据管理（必须在 selectedDay 定义之后）
  const { routeSpots, routeSpotsLoading } = useRouteMap({
    showRouteMap,
    selectedDay,
    supabaseClient
  });
  
  // Sheet bottom padding (safe area for mobile bottom nav)
  const sheetBottomPaddingNumber = typeof window !== 'undefined' && window.innerHeight > 0 ? Math.max(0, window.innerHeight * 0.08) : 64;
  const sheetBottomPadding = `${sheetBottomPaddingNumber}px`;

  // Floating action buttons visibility:
  // Per new UX: these two icon-only buttons appear ONLY when NOT in edit mode
  // (so they never show together with the + edit FAB which is only shown in edit mode).
  const floatingButtonsVisible = !isInEditMode && !showRouteMap && !quickAddOpen && !(openPoi && openPoi.visible) && !openCustomEditor && !timePickerVisible && currentSubView !== 'select-card' && !sheetExpanded;

  // Compute an appropriate bottom offset for the right-bottom stack so it stays above any bottom nav / safe area.
  // If fabBottomOffset is small (default 24), it implies no nav bar detected.
  // But on mobile web (isNarrow), we often have one that might be missed or we just want to be safe.
  const effectiveFabBottom = Math.max(fabBottomOffset || 0, isNarrow ? 90 : 24);
  const fabButtonsBottom = effectiveFabBottom + 16;
  
  // Handle sheet drag for drawer behavior (支持向上推全屏、向下拉关闭)
  const handleSheetPointerDown = (e: React.PointerEvent | React.TouchEvent) => {
    e.stopPropagation();
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
    dragStartHeight.current = sheetHeight;
    setIsDragging(true);
  };

  const handleSheetPointerMove = (e: PointerEvent | TouchEvent) => {
    if (!isDragging) return;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const deltaY = dragStartY.current - clientY;
    const viewportHeight = window.innerHeight;
    const deltaPercent = (deltaY / viewportHeight) * 100;
    let newHeight = dragStartHeight.current + deltaPercent;
    // 限制范围: 30% (最小) 到 90% (最大，接近全屏)
    newHeight = Math.max(30, Math.min(90, newHeight));
    setSheetHeight(newHeight);
  };

  const handleSheetPointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    // 吸附逻辑: < 40% 关闭, 40-80% 回到 70%, > 80% 全屏
    if (sheetHeight < 40) {
      closeRouteMap();
    } else if (sheetHeight > 80) {
      setSheetHeight(90); // snap to full screen
    } else {
      setSheetHeight(70); // snap to default
    }
  };

  useEffect(() => {
    if (isDragging) {
      const move = (e: any) => handleSheetPointerMove(e);
      const up = () => handleSheetPointerUp();
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('touchmove', move);
      window.addEventListener('touchend', up);
      return () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('touchmove', move);
        window.removeEventListener('touchend', up);
      };
    }
  }, [isDragging, sheetHeight]);
  
  // Handler for day tab selection
  const handleSelectDay = (dayIndex: number) => {
    try {
      // update editor state
      editor.setSelectedDayIndex?.(typeof dayIndex === 'number' ? dayIndex : 0);
      // ensure main timeline view is visible
      setCurrentSubView('timeline');
      // notify parent if interested
      try { if (typeof onDaySelect === 'function') onDaySelect(dayIndex); } catch (e) {}
    } catch (e) {
      console.warn('[ItineraryEditor] handleSelectDay error', e);
    }
  };

  console.log('[ItineraryEditor] Current days:', JSON.stringify(days.map((d, i) => ({ 
    index: i, 
    day: d.day, 
    date: d.date, 
    itemCount: d.items?.length || 0 
  })), null, 2));

  console.log('🚀 ItineraryEditor RENDER', {
    hasInitialFormData: !!initialFormData,
    location: initialFormData?.location,
    currency: initialFormData?.currency,
  });

  const resolvedTitle = (editor.itinerary && (editor.itinerary.title || editor.itinerary.name))
    || (initialItinerary && (initialItinerary.name || initialItinerary.title))
    || (initialFormData && (initialFormData.name || initialFormData.title))
    || '行程编辑';
  const resolvedSubtitle = (editor.itinerary && (editor.itinerary.location || editor.itinerary.destination_city || editor.itinerary.custom_city))
    || (initialItinerary && (initialItinerary.location || initialItinerary.destination_city || initialItinerary.custom_city))
    || (initialFormData && (initialFormData.location || initialFormData.custom_city || initialFormData.selectedCity))
    || undefined;
  const resolvedCity = resolvedSubtitle;
  const resolvedCoverImage = (editor.itinerary && ((editor.itinerary as any).background_image_url || (editor.itinerary as any).cover_image)) || bgSource?.uri || undefined;

  const coverInputRef = useRef<HTMLInputElement>(null);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!currentUserId) {
      showError && showError('無法獲取用戶 ID，請重新登入');
      return;
    }
    
    try {
      showInfo && showInfo('正在上傳封面...');
      const formData = new FormData();
      formData.append('file', file);
      
      // Use group-specific folder if available, otherwise temp
      const folderPath = groupIdForLocal ? `group/${groupIdForLocal}/cover` : 'temp/cover';
      formData.append('folder', folderPath);
      
      formData.append('userId', currentUserId);
      
      const CARD_IMAGE_UPLOAD_ENDPOINT = (typeof window !== 'undefined' && (window as any).__CARD_IMAGE_UPLOAD__) || 'https://card-image-upload.cubemeet.app';
      
      const res = await fetch(CARD_IMAGE_UPLOAD_ENDPOINT, {
        method: 'POST',
        body: formData,
        mode: 'cors',
      });
      
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Upload failed: ${res.status} ${text}`);
      }
      
      const result = await res.json();
      const url = result.url || result.secure_url || (result.data && result.data.url);
      
      if (url) {
         if (groupIdForLocal && supabaseClient) {
           await supabaseClient.from('groups').update({ background_image_url: url }).eq('id', groupIdForLocal);
         }
         
         if (editor.itinerary) {
           editor.setItinerary({ ...editor.itinerary, background_image_url: url });
         }
         
         showSuccess && showSuccess('封面已更新');
      } else {
        throw new Error('Invalid response: no URL found');
      }
    } catch (err) {
      console.error(err);
      showError && showError(`上傳失敗: ${err instanceof Error ? err.message : '未知錯誤'}`);
    } finally {
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleSaveSettings = async (data: { title: string; coverUrl?: string; startDate?: string; currency?: string; location?: string }) => {
    try {
      if (!groupIdForLocal || !supabaseClient) return;
      
      const updates: any = {
        name: data.title,
        background_image_url: data.coverUrl,
        start_date: data.startDate,
        currency: data.currency,
        location: data.location, // Assuming 'location' column exists or mapped to destination_city
      };

      const { error } = await supabaseClient.from('groups').update(updates).eq('id', groupIdForLocal);
      
      if (error) throw error;

      // Update local state
      if (editor.itinerary) {
        let newDays = editor.itinerary.days;

        // If start date changed, recalculate dates for all days to ensure consistency
        if (data.startDate) {
          // Parse YYYY-MM-DD manually to avoid timezone shifts (treat as local date)
          const parts = data.startDate.split('-');
          if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const day = parseInt(parts[2], 10);

            const start = new Date(year, month, day); // Local time

            const pad = (n: number) => String(n).padStart(2, '0');
            newDays = (editor.itinerary.days || []).map((d: any, i: number) => {
              const dt = new Date(start);
              dt.setDate(dt.getDate() + i);
              const dateStr = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
              return { ...d, date: dateStr };
            });
          }
        }

        const newItinerary = { ...editor.itinerary, ...updates, title: data.title, days: newDays };
        editor.setItinerary(newItinerary);
      }
      
      // Also update initialFormData/initialItinerary if possible (shallow)
      try {
        if (initialFormData) {
          initialFormData.name = data.title;
          initialFormData.location = data.location;
          initialFormData.currency = data.currency;
        }
      } catch (e) {
        console.warn('Failed to mutate initialFormData', e);
      }

      showSuccess && showSuccess('設置已保存');
      setShowSettingsModal(false);
    } catch (e) {
      console.error('Save settings failed', e);
      showError && showError('保存設置失敗');
    }
  };

  return (
    <View style={styles.container}>
      <input 
        type="file" 
        ref={coverInputRef} 
        style={{ display: 'none' }} 
        accept="image/*" 
        onChange={handleCoverUpload} 
      />
      
      <View style={styles.mainContainer}>
        {/* Header area should remain full-width */}
        <View style={styles.headerContainer}>
          <EditorHeader
            title={resolvedTitle}
            subtitle={resolvedSubtitle}
            backgroundSource={resolvedCoverImage ? { uri: resolvedCoverImage } : bgSource}
            onBack={onBack}
            showLocationBadge={showLocationBadge}
            showRightAction={false}
          />
          {/* owner-only toggle rendered at header right circle position with label */}
          <View style={styles.teamEditToggleAtRightCircle}>
            {canEdit && (
              <>
                <TouchableOpacity 
                  onPress={() => setShowMoreMenu(prev => !prev)} 
                  activeOpacity={0.85}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.3)'
                  }}
                >
                  <MdMoreHoriz size={24} color="#fff" />
                </TouchableOpacity>
                
                <ItineraryMoreMenu
                  visible={showMoreMenu}
                  onClose={() => setShowMoreMenu(false)}
                  onSettings={() => setShowSettingsModal(true)}
                  onChangeCover={() => coverInputRef.current?.click()}
                  isOwner={isOwner}
                  allowTeamEdit={allowTeamEdit}
                  onToggleTeamEdit={(val) => persistAllowTeamEdit(val)}
                  isInEditMode={isInEditMode}
                  onToggleEditMode={() => setIsInEditMode(prev => !prev)}
                />
              </>
            )}
          </View>

          <View style={styles.tabsWrapper}>
            <ItineraryTabs days={days} selectedIndex={editor.selectedDayIndex} onSelect={handleSelectDay} isEditing={false} onAddDay={() => editor.addDay()} />
          </View>
        </View>

        {/* Centered page content below header */}
        <View style={styles.pageInner}>
        {/* ShareButton - 显示在 header 和 timeline 之间，不被遮挡 */}
        {(() => {
          console.log('[ItineraryEditor] ShareButton condition check:', { showShareButton, contentType, contentId, creator: creator ? { id: creator.id, name: creator.display_name } : null });
          return showShareButton && contentType && contentId && creator && (
            <ShareButton
              type={contentType}
              id={contentId}
              title={(initialItinerary && (initialItinerary.name || initialItinerary.title)) || (initialFormData && (initialFormData.name || initialFormData.title)) || '分享内容'}
              description={(initialItinerary && initialItinerary.location) || (initialFormData && initialFormData.location) || ''}
              imageUrl={bgSource?.uri}
              creator={creator}
              containerStyle={{ marginBottom: 0, paddingBottom: 8 }}
              onCreatorPress={() => navigate(`/user/${creator.id}`)}
              userRelation={{ isFollowing, isLiked: isCreatorLiked }}
              onLikePress={handleCreatorLikePress}
              onFollowPress={async (targetId?: string) => {
                try {
                  if (!targetId) return;
                  const client = supabaseClient ?? (await import('@cubemeet/core/services/supabase')).supabase;
                  const authRes: any = await client.auth.getUser();
                  const currentUser = authRes?.data?.user || (client.auth && (client.auth.user && client.auth.user()));
                  console.log('[ItineraryEditor] Follow attempt currentUser=', currentUser && { id: currentUser.id, email: currentUser.email });
                  console.log('[ItineraryEditor] Follow attempt targetId=', targetId);
                  if (!currentUser) {
                    const ref = typeof window !== 'undefined' ? encodeURIComponent(window.location.pathname + window.location.search) : '/';
                    navigate(`/auth?referer=${ref}`);
                    return;
                  }

                  console.log('[ItineraryEditor] Calling client.from("friends").upsert...');
                  const { data, error } = await client
                    .from('friends')
                    .upsert({ user_id: currentUser.id, friend_id: targetId, status: 'active' }, { onConflict: 'user_id,friend_id' })
                    .select()
                    .single();
                  console.log('[ItineraryEditor] Follow upsert result:', { data, error });
                  try { console.log('[ItineraryEditor] Follow upsert result (stringified):', JSON.stringify(data, null, 2)); } catch (e) { }

                  if (error) {
                    console.error('Follow upsert error:', error);
                    const msg = String(error.message || error?.details || '关注失败');
                    if (/conflict|duplicate|unique|23505|409/i.test(msg) || (error.status === 409)) {
                      try {
                        const { data: existing } = await client
                          .from('friends')
                          .select('*')
                          .eq('user_id', currentUser.id)
                          .eq('friend_id', targetId)
                          .maybeSingle();
                        if (existing) {
                          setIsFollowing(true);
                          showSuccess && showSuccess('已關注');
                          return;
                        }
                      } catch (checkErr) {
                        console.error('Follow existence check failed:', checkErr);
                      }
                      setIsFollowing(true);
                      showSuccess && showSuccess('已關注');
                      return;
                    }
                    showError && showError(msg);
                    return;
                  }

                  setIsFollowing(true);
                  showSuccess && showSuccess('已關注');
                } catch (e: any) {
                  console.error('Unexpected follow error:', e);
                  try { showError && showError(String(e?.message || '关注失败')); } catch (er) { console.error(er); }
                }
              }}
            />
          );
        })()}

        {/* Travel Dashboard Widget - 显示天气、汇率、当地时间 */}
        {(() => {
          console.log('=== TravelDashboardWidget Render Check ===', {
            hasInitialFormData: !!initialFormData,
            location: initialFormData?.location,
            currency: initialFormData?.currency,
            willRender: !!(initialFormData && initialFormData.location),
          });
          
          if (!initialFormData) {
            console.log('No initialFormData, skipping dashboard');
            return null;
          }
          
          if (!initialFormData.location) {
            console.log('No location in initialFormData, skipping dashboard');
            return null;
          }
          
          console.log('Rendering TravelDashboardWidget with:', {
            location: initialFormData.location,
            baseCurrency: initialFormData.currency || 'CNY',
          });
          
          return (
            <TravelDashboardWidget
              location={initialFormData.location}
              baseCurrency={initialFormData.currency || 'CNY'}
            />
          );
        })()}

        {currentSubView === 'add-item' ? (
          <CardFormHost
            visible={true}
            cardType={(createForm && createForm.type) || 'poi'}
            initialData={createForm}
            onClose={() => setCurrentSubView('timeline')}
            onSave={async (data: any) => {
              try {
                // Prepare itinerary copy
                const dayIndex = editor.selectedDayIndex || 0;
                const it = JSON.parse(JSON.stringify(editor.itinerary || { days: [] }));
                while ((it.days || []).length <= dayIndex) it.days.push({ day: it.days.length + 1, items: [] });

                // Merge payload (shopping special-case preserved)
                let payload;
                if (createForm.type === 'shopping') {
                  payload = { ...createEmptyShoppingPayload(), ...data };
                } else {
                  payload = { ...(createForm.payload || {}), ...data };
                }

                // Determine suggested time (one hour after last item if present)
                let itemTime = '09:00';
                const allItemsInDay = it.days[dayIndex]?.items || [];
                if (allItemsInDay.length > 0) {
                  const lastItem = allItemsInDay[allItemsInDay.length - 1];
                  if (lastItem.time && lastItem.time.match(/^\d{1,2}:\d{2}$/)) {
                    const timeParts = lastItem.time.split(':');
                    let hour = parseInt(timeParts[0], 10);
                    hour = (hour + 1) % 24;
                    itemTime = `${String(hour).padStart(2, '0')}:00`;
                  }
                }

                // 获取当前用户ID
                let currentUserId: string | null = null;
                try {
                  const userRes = await supabaseClient.auth.getUser();
                  currentUserId = userRes?.data?.user?.id || null;
                } catch (e) {
                  console.warn('[CardFormHost] 无法获取当前用户ID', e);
                }

                // Build in-memory item skeleton (id may be replaced by server id for persisted entries)
                const localId = genUniqueId();
                const item = {
                  id: localId,
                  type: createForm.type,
                  title: data.title || createForm.title || (createForm.type === 'poi' ? '新景點' : '新條目'),
                  time: itemTime,
                  note: createForm.note || undefined,
                  payload,
                } as any;

                // 尝试持久化到数据库（所有类型）
                const groupId = groupIdForLocal;
                const shouldPersist = supabaseClient && groupId && canEdit;
                
                if (shouldPersist) {
                  try {
                    const dayNumber = (dayIndex || 0) + 1;
                    const dayDate = (it.days[dayIndex] && it.days[dayIndex].date) ? it.days[dayIndex].date : null;
                    const sortOrder = (it.days[dayIndex].items || []).length;

                    // 构建通用的custom_payload（根据卡片类型）
                    let customPayload: any = null;
                    let itemType = 'custom';
                    let startTime: string | null = null;
                    let endTime: string | null = null;

                    if (createForm.type === 'transport') {
                      // Build transport JSON payload consistent with provided example
                      itemType = 'transport';
                      const transportJson: any = {
                        to: data.to || data.destination || payload.to || payload.destination || null,
                        code: data.code || data.identifier || payload.code || null,
                        from: data.from || data.origin || payload.from || payload.origin || null,
                        mode: data.mode || data.transport_type || payload.mode || payload.transport_type || 'ferry',
                        type: 'transport',
                        title: data.title || payload.title || (data.code ? `${(data.mode || '交通').toString()}${data.code}` : '交通'),
                        start_time: (data.start_time || payload.start_time || null),
                        end_time: (data.end_time || payload.end_time || null),
                        // include operator fields when present so logo and carrier info persist
                        operator: data.operator || payload.operator || null,
                        operator_iata: data.operator_iata || payload.operator_iata || null,
                      };

                      // Normalize times to HH:mm:ss if they are HH:mm
                      const normalizeTime = (v: any) => {
                        if (!v) return null;
                        const s = String(v);
                        if (/^\d{1,2}:\d{2}$/.test(s)) return `${s}:00`;
                        return s;
                      };
                      transportJson.start_time = normalizeTime(transportJson.start_time);
                      transportJson.end_time = normalizeTime(transportJson.end_time);
                      
                      customPayload = transportJson;
                      startTime = transportJson.start_time;
                      endTime = transportJson.end_time;
                    } else {
                      // 其他类型（餐饮、住宿、景点等）
                      customPayload = {
                        type: createForm.type,
                        title: data.title || createForm.title || '新条目',
                        ...payload,
                      };
                      // 如果payload中有时间，提取出来
                      if (payload.start_time) startTime = payload.start_time;
                      if (payload.end_time) endTime = payload.end_time;
                    }

                    const row: any = {
                      group_id: groupId,
                      day_number: dayNumber,
                      day_date: dayDate,
                      sort_order: sortOrder,
                      item_type: itemType,
                      card_id: null,
                      note: createForm.note || null,
                      start_time: startTime,
                      end_time: endTime,
                      custom_payload: customPayload,
                      inline_payload: customPayload,
                      creator_id: currentUserId,
                    };

                    console.log('🔍 [CardFormHost] 准备插入 row:', row);

                    const insertRes = await supabaseClient.from('group_itinerary_entries').insert([row]).select().maybeSingle();
                    const inserted = insertRes && (insertRes.data ?? insertRes);
                    const insertErr = insertRes && (insertRes.error ?? (insertRes as any).error);

                    console.log('🔍 [CardFormHost] 插入结果:', { inserted, insertErr });

                    if (!insertErr && inserted) {
                      // Use server id as the itinerary entry id
                      const serverId = inserted.id || inserted[0]?.id || null;
                      console.log('🔍 [CardFormHost] serverId =', serverId, 'type:', typeof serverId);
                      
                      if (serverId) {
                        item.id = serverId;
                        item.meta = { itineraryEntryId: serverId, itemType };
                        item.payload = customPayload;
                        if (inserted.creator_id) item.creator_id = inserted.creator_id;

                        it.days[dayIndex].items = [...allItemsInDay, item];
                        editor.setItinerary(it);
                        setDirty(true);
                        
                        const typeNames: Record<string, string> = {
                          transport: '交通',
                          restaurant: '餐饮',
                          lodging: '住宿',
                          poi: '景点',
                          activity: '活动',
                          shopping: '购物',
                        };
                        try { showSuccess && showSuccess(`${typeNames[createForm.type] || '卡片'}已保存`); } catch (e) {}
                        setCurrentSubView('timeline');
                        return;
                      }
                    }

                    console.error('[CardFormHost] insert error:', insertErr, inserted);
                    try { showError && showError('保存至伺服器失敗，已在本地新增'); } catch (e) {}
                    // fallthrough to local add
                  } catch (e) {
                    console.error('[CardFormHost] persist unexpected error:', e);
                    try { showError && showError('保存失敗，已在本地新增'); } catch (er) {}
                    // fallthrough to local add
                  }
                }

              // Fallback / default: add locally to itinerary
              it.days[dayIndex].items = [...allItemsInDay, item];
              editor.setItinerary(it);
              setDirty(true);
            } catch (e) {
              console.warn('create item failed', e);
            }
            setCurrentSubView('timeline');
          }}
        />
      ) : (
        <>
            <TimelineList
            items={selectedDay?.items || []}
            selectedDay={selectedDay}
            isNarrow={isNarrow}
            isEditing={canEdit && isInEditMode}
            scrollToIndex={timelineScrollToIndex}
            parseTimeToMinutes={parseTimeToMinutes}
            getNowMinutes={getNowMinutes}
            expandedCards={expandedCards}
            toggleExpand={(cardId: string) => setExpandedCards(prev => { const next = new Set(prev); if (next.has(cardId)) next.delete(cardId); else next.add(cardId); return next; })}
            editor={editor}
            setDirty={setDirty}
            setOpenPoi={(item: any, itemIndex: number, context?: { fromPlaceholder?: boolean; payloadOverride?: any }) => {
              try {
                // --- START OF MODIFICATION ---
                const explicitPlaceholder = context?.fromPlaceholder === true;
                const overridePayload = context?.payloadOverride;
                const modalPayload = (() => {
                  if (overridePayload && typeof overridePayload === 'object') return overridePayload;
                  return item.custom_payload || item.inline_payload || item.payload || {};
                })();
                const placeholderFlag = (() => {
                  try {
                    const meta = item?.meta || {};
                    const raw = meta.isPlaceholder ?? meta.placeholder ?? meta.is_placeholder;
                    if (raw === true) return true;
                    if (typeof raw === 'string') return ['1', 'true', 'yes', 'on'].includes(raw.trim().toLowerCase());
                    if (typeof raw === 'number') return raw !== 0;
                    return false;
                  } catch (err) {
                    return false;
                  }
                })();
                const shouldForceEdit = explicitPlaceholder || placeholderFlag;
                const fromPlaceholder = shouldForceEdit;

                // To ensure title and other fields are correctly displayed, merge top-level item properties.
                const finalPayload = {
                  ...modalPayload,
                  title: item.title || modalPayload.title,
                  note: item.note || modalPayload.note,
                  // Ensure the original card object is NOT passed if we are isolating.
                  card: undefined, 
                  // Pass the full item so the modal can still access id, type, etc. if needed.
                  _item: item,
                  _forceEdit: shouldForceEdit ? true : undefined,
                  _fromPlaceholder: explicitPlaceholder ? true : undefined,
                };
                // --- END OF MODIFICATION ---

                // Always open modal when clicking from the itinerary (desktop and mobile)
                setOpenPoi({ 
                  visible: true, 
                  payload: finalPayload, 
                  dayIndex: editor.selectedDayIndex, // Pass current day index
                  itemIndex: itemIndex,             // Pass the item's index in the day
                  forceEdit: shouldForceEdit,
                  fromPlaceholder,
                });
                return;
              } catch (e) {
                // safest fallback: open modal
                const shouldForceEdit = Boolean(context?.fromPlaceholder || item?.meta?.isPlaceholder || item?.meta?.placeholder || item?.meta?.is_placeholder);
                setOpenPoi({ visible: true, payload: item, dayIndex: editor.selectedDayIndex, itemIndex, forceEdit: shouldForceEdit, fromPlaceholder: shouldForceEdit });
              }
            }}
            setInlineEditTarget={(t:any) => {
              if (!t) { setInlineEditTarget(null); setInlineEditTime(undefined); return; }
              const { dayIndex, itemIndex, mode } = t;
              const item = (editor.itinerary?.days?.[dayIndex]?.items || [])[itemIndex];
              // store full target including mode (e.g. 'time' or 'content')
              setInlineEditTarget({ dayIndex, itemIndex, mode });
              if (mode === 'time') {
                setInlineEditTime(item?.time || '09:00');
              } else {
                setInlineEditTime(undefined);
              }
            }}
            inlineEditTarget={inlineEditTarget}
            supabaseClient={supabaseClient}
            currentUserId={currentUserId}
            canEdit={canEdit}
            groupIdForLocal={groupIdForLocal}
            memberProfiles={memberProfiles}
            onSelectFromCardPicker={(dayIndex, itemIndex) => {
              console.log('[ItineraryEditor] 占位卡片点击 CardPicker', { dayIndex, itemIndex });

              const item = (editor.itinerary?.days?.[dayIndex]?.items || [])[itemIndex];
              const cardType = item?.card_type || item?.type || 'poi';

              setPlaceholderCardInfo({
                dayIndex,
                itemIndex,
                cardType,
              });

              setCurrentSubView('select-card');
            }}
            onImageClick={(images, startIndex) => {
              console.log('[ItineraryEditor] onImageClick triggered:', { imageCount: images.length, startIndex });
              setLightboxState({ images, currentIndex: startIndex });
            }}
          />

          {/* Fullscreen edit modal for non-transport cards */}
          {/* Inline edit modal removed — editing happens in Fullscreen POI modal */}
          {/* Time wheel inline editor modal */}
          <TimeWheelPicker
            visible={inlineEditTarget?.mode === 'time'}
            value={inlineEditTime}
            onChange={(v) => setInlineEditTime(v)}
            onCancel={() => { setInlineEditTarget(null); setInlineEditTime(undefined); }}
            onConfirm={() => {
              if (inlineEditTarget && inlineEditTarget.mode === 'time') {
                const { dayIndex, itemIndex } = inlineEditTarget;
                const newTime = inlineEditTime || '09:00';
                console.log('[TimeWheelPicker] onConfirm:', { dayIndex, itemIndex, newTime, inlineEditTime });
                try {
                  const beforeItem = editor.itinerary?.days?.[dayIndex]?.items?.[itemIndex];
                  console.log('[TimeWheelPicker] before update:', beforeItem);
                  
                  editor.setItemTime(dayIndex, itemIndex, newTime);
                  
                  const afterItem = editor.itinerary?.days?.[dayIndex]?.items?.[itemIndex];
                  console.log('[TimeWheelPicker] after update:', afterItem);
                  
                  setDirty(true);
                  try { showSuccess && showSuccess(`時間已更新為 ${newTime}`); } catch (e) {}
                } catch (e) {
                  console.error('[TimeWheelPicker] error:', e);
                  try { showError && showError('更新時間失敗'); } catch (er) {}
                }
              }
              setInlineEditTime(undefined);
            }}
          />
        </>
      )}
      {/* Inline edit UI removed. Editing and delete now performed inside FullscreenPoiModal according to permissions. */}
      {/* Web: floating add-item FAB for editing mode */}
  {canEdit && isInEditMode && currentSubView === 'timeline' && !inlineEditTarget && !(openPoi && openPoi.visible) && !openCustomEditor && !quickAddOpen && !quickAddClosing ? (
        <FloatingActionButton onPress={() => setQuickAddOpen(prev => !prev)} bottomOffset={fabBottomOffset}>
          {/* show + only when closed; hide FAB while quick-add menu is open (backdrop closes it) */}
          <Text style={{ color: '#fff', fontWeight: '700', fontSize: 20, lineHeight: 44, textAlign: 'center' }}>＋</Text>
        </FloatingActionButton>
      ) : null}

  <QuickAddMenu
    visible={quickAddOpen}
    onClose={() => setQuickAddOpen(false)}
    renderIcon={renderCustomIcon}
    showBottomRight={false}
    onSelect={(type) => {
      // 新逻辑：transport 应直接打开 Transport 表单，其它卡片保持占位行为
      // 用户可以在占位卡片内选择：1) 去 CardPicker 拉入 2) 打开全屏模态自己创建

      if (type === 'other') {
        // "其他" 类型：直接跳转到卡片选择器
        setCurrentSubView('select-card');
        setQuickAddOpen(false);
        return;
      }

      if (type === 'note') {
        // "笔记/自定义"：打开自定义编辑器
        setOpenCustomEditor(true);
        setQuickAddOpen(false);
        return;
      }

      if (type === 'transport') {
        // For transport, open the transport form directly instead of creating a placeholder
        setCreateForm({ type: 'transport', title: '', time: '09:00', note: '', payload: {}, mode: 'create' });
        setCurrentSubView('add-item');
        setQuickAddOpen(false);
        return;
      }

      try {
        // 对于卡片类型（poi, meal, activity, lodging, shopping）以及“外部链接”(file)
        // 在时间轴添加一个空的占位卡片
        if (type === 'video') {
          const dayIndex = editor.selectedDayIndex || 0;
          // add a local placeholder item
          editor.addItem(dayIndex);
          const items = editor.itinerary?.days?.[dayIndex]?.items || [];
          const idx = items.length - 1;
          const newItem = items[idx] as any;
          if (newItem) {
            const payload: any = {
              type: 'external',
              website: null,
              imageUrl: null,
              siteName: null,
              favicon: null,
              partner: null,
              resolvedFinalUrl: null,
              degraded: null,
              source: { kind: 'manual-paste', at: new Date().toISOString() },
              embed: { kind: 'iframe', src: '' }
            };
            const patch: any = {
              type: 'external',
              meta: {
                ...(newItem.meta || {}),
                isPlaceholder: true,
                externalOnly: true,
                videoEmbedOnly: true,
              },
              title: '',
              payload,
              custom_payload: payload,
            };
            editor.updateItem?.(dayIndex, idx, patch);
            setDirty(true);
            setTimelineScrollToIndex(idx);
            setTimeout(() => setTimelineScrollToIndex(null), 600);
          }
          setQuickAddOpen(false);
          return;
        }
        const dayIndex = editor.selectedDayIndex || 0;
        editor.addItem(dayIndex);
        const items = editor.itinerary?.days?.[dayIndex]?.items || [];
        const idx = items.length - 1;
        const newItem = items[idx] as any;

        if (newItem) {
          // 当用户点了曲别针（type === 'file'）时，我们创建 external 占位卡片
          const resolvedType = (type === 'file') ? 'external' : (type || 'poi');
          const patch: any = {
            type: resolvedType,
            meta: {
              ...(newItem.meta || {}),
              isPlaceholder: true,
              // external-only 占位标志：TimelineItem 将据此显示精简的粘贴 UI
              externalOnly: resolvedType === 'external'
            },
            title: ''
          };
          editor.updateItem?.(dayIndex, idx, patch);
          setDirty(true);
          // 将新占位滚动到视口中间
          setTimelineScrollToIndex(idx);
          // 稍后清理指令，避免后续渲染重复触发
          setTimeout(() => setTimelineScrollToIndex(null), 600);
        }
      } catch (e) {
        console.error('quick add placeholder failed', e);
      } finally {
        setQuickAddOpen(false);
      }
    }}
  />

      <PoiModalWrapper
        openPoi={openPoi}
        setOpenPoi={setOpenPoi}
        editor={editor}
        isNarrow={isNarrow}
        supabaseClient={supabaseClient}
        currentUserId={currentUserId}
        allowTeamEdit={allowTeamEdit}
        isCollaborator={isCollaborator}
        groupIdForLocal={groupIdForLocal}
        ownerId={ownerId}
        setDirty={setDirty}
        // --- START OF MODIFICATION ---
        // Pass the item's location down to the wrapper
        dayIndex={openPoi.dayIndex}
        itemIndex={openPoi.itemIndex}
        // Pass the page-level creator/profile down so modal can reuse it and avoid refetching
        creator={creator}
        // --- END OF MODIFICATION ---
      />
      <FullscreenCustomEditorModal visible={openCustomEditor} onClose={() => setOpenCustomEditor(false)} />

      {/* Time picker modal for adding card from CardPicker */}
      {timePickerVisible && pendingCard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: '20px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>
              選擇時間 - {pendingCard.title}
            </h3>
            <input
              type="time"
              defaultValue={createForm.time || '09:00'}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: 16,
                border: '1px solid #ddd',
                borderRadius: 6,
                marginBottom: 16,
                boxSizing: 'border-box'
              }}
              onChange={(e) => setCreateForm({ ...createForm, time: e.target.value })}
            />
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={async () => {
                  setTimePickerVisible(false);
                  setPendingCard(null);
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 16,
                  cursor: 'pointer'
                }}
              >
                取消
              </button>
              <button
                onClick={async () => {
                  // Add card with selected time
                  // IMPORTANT: copy-on-add behavior — snapshot the selected card into custom_payload
                  // so subsequent edits operate on this copied payload instead of mutating the original
                  const id = genUniqueId();
                  const dayIndex = editor.selectedDayIndex || 0;
                  const it = JSON.parse(JSON.stringify(editor.itinerary || { days: [] }));
                  while ((it.days || []).length <= dayIndex) it.days.push({ day: it.days.length + 1, items: [] });

                  // 🔍 检查是否已存在相同的卡片（同一天内）
                  const currentDayItems = it.days[dayIndex]?.items || [];
                  const cardIdToAdd = pendingCard?.id;
                  
                  console.log('[ItineraryEditor] 重复检查开始', {
                    cardIdToAdd,
                    pendingCardTitle: pendingCard?.title,
                    dayIndex,
                    当前天数项目数: currentDayItems.length,
                    当前天所有卡片ID: currentDayItems.map((item: any) => ({
                      itemId: item?.id,
                      cardId: item?.card?.id,
                      metaCardId: item?.meta?.cardId,
                      card_id: item?.card_id,
                      title: item?.title,
                    }))
                  });
                  
                  const isDuplicate = currentDayItems.some((item: any) => {
                    const existingCardId = item?.card?.id || item?.meta?.cardId || item?.card_id;
                    const matches = existingCardId && existingCardId === cardIdToAdd;
                    if (matches) {
                      console.log('[ItineraryEditor] 🚫 发现重复卡片!', {
                        existingCardId,
                        cardIdToAdd,
                        existingTitle: item?.title,
                        newTitle: pendingCard?.title,
                      });
                    }
                    return matches;
                  });

                  if (isDuplicate) {
                    console.warn('[ItineraryEditor] ❌ 卡片已存在于当天行程中，拒绝添加重复卡片', {
                      cardId: cardIdToAdd,
                      title: pendingCard?.title,
                      dayIndex,
                    });
                    showWarning && showWarning('该卡片已在当天行程中，无法重复添加');
                    setTimePickerVisible(false);
                    setPendingCard(null);
                    return;
                  }
                  
                  console.log('[ItineraryEditor] ✅ 没有重复，继续添加卡片');

                  // Simple authoritative-copy strategy:
                  // Fetch `base_payload` directly from `cards_cards` by id and use that as the snapshot.
                  // This avoids complex client-side merging and guarantees we use the DB-authoritative payload.
                  let custom_payload: any = null;
                  try {
                    if (supabaseClient && pendingCard?.id) {
                      try {
                        const { data: cardRow, error } = await supabaseClient.from('cards_cards').select('base_payload,payload').eq('id', pendingCard.id).maybeSingle();
                        if (error) {
                          console.warn('[ItineraryEditor] fetch cards_cards.base_payload error', error);
                        }
                        if (!error && cardRow) {
                          // prefer explicit base_payload column; fallback to payload column if present
                          custom_payload = cardRow.base_payload ?? cardRow.payload ?? null;
                        }
                      } catch (e) {
                        // ignore and fallback below
                      }
                    }
                  } catch (e) {}

                  // Minimal fallback if DB fetch failed or not available: use any existing base_payload/payload on pendingCard
                  if (!custom_payload) {
                    custom_payload = pendingCard?.base_payload ?? pendingCard?.payload ?? pendingCard ?? {};
                  }

                  // Ensure we have a deep-cloned object to avoid accidental mutation of source rows
                  try { custom_payload = JSON.parse(JSON.stringify(custom_payload || {})); } catch (e) { custom_payload = (custom_payload || {}); }

                  // DEBUG: print the snapshot we're about to attach so QA can verify fields (remove later)
                  try { console.debug('[ItineraryEditor] constructed custom_payload snapshot:', custom_payload); } catch (e) {}

                  // Determine final item.type: prefer createForm.type (which we set from card),
                  // otherwise use card_type/payload type from the snapshot, fallback to 'poi'
                  const resolvedType = (createForm && createForm.type) || custom_payload.type || custom_payload.card_type || 'poi';

                  const item = {
                    id,
                    type: resolvedType,
                    title: pendingCard.title || '新條目',
                    time: createForm.time,
                    note: pendingCard.description || '',
                    // keep a reference to original card for auditing if needed, but primary editable source is custom_payload
                    card: pendingCard,
                    payload: custom_payload,
                    custom_payload,
                    meta: { cardId: pendingCard.id, itemType: 'card' }
                  };
                  // Try to persist the new itinerary entry to DB when possible (copy-on-add snapshot)
                  try {
                    if (supabaseClient && groupIdForLocal && canEdit) {
                      const dayNumber = (dayIndex || 0) + 1;
                      const dayDate = (it.days[dayIndex] && it.days[dayIndex].date) ? it.days[dayIndex].date : null;
                      const sortOrder = (it.days[dayIndex].items || []).length;

                      // 获取当前用户ID
                      let currentUserId: string | null = null;
                      try {
                        const userRes = await supabaseClient.auth.getUser();
                        currentUserId = userRes?.data?.user?.id || null;
                      } catch (e) {
                        console.warn('[ItineraryEditor] 无法获取当前用户ID', e);
                      }

                      const row: any = {
                        group_id: groupIdForLocal,
                        day_number: dayNumber,
                        day_date: dayDate,
                        sort_order: sortOrder,
                        item_type: 'card',
                        card_id: pendingCard.id || null,
                        note: pendingCard.description || null,
                        start_time: (createForm.time && String(createForm.time).length > 0) ? (String(createForm.time).length === 5 ? `${createForm.time}:00` : String(createForm.time)) : null,
                        custom_payload: custom_payload || null,
                        inline_payload: null,
                        creator_id: currentUserId,
                      };

                      console.log('🔍 [ItineraryEditor] 准备插入卡片副本 row:', row);

                      try {
                        const insertRes = await supabaseClient.from('group_itinerary_entries').insert([row]).select().maybeSingle();
                        const inserted = insertRes && (insertRes.data ?? insertRes);
                        const insertErr = insertRes && (insertRes.error ?? (insertRes as any).error);
                        
                        console.log('🔍 [ItineraryEditor] 插入结果:', { inserted, insertErr });
                        
                        if (!insertErr && inserted) {
                          // Replace local id with server id and keep custom_payload persisted
                          const serverId = inserted.id || (Array.isArray(inserted) ? inserted[0]?.id : null);
                          console.log('🔍 [ItineraryEditor] serverId =', serverId, 'type:', typeof serverId);
                          
                          if (serverId) {
                            item.id = serverId;
                            item.meta = { ...(item.meta || {}), itineraryEntryId: serverId, itemType: 'card' };
                            // ensure the item's custom_payload matches persisted row
                            item.custom_payload = inserted.custom_payload || custom_payload;
                            // attach server timestamps when available
                            if (inserted.created_at) item.created_at = inserted.created_at;
                            if (inserted.updated_at) item.updated_at = inserted.updated_at;
                            if (inserted.creator_id) item.creator_id = inserted.creator_id;
                          }
                        } else {
                          console.warn('[ItineraryEditor] persist new card entry failed, falling back to local add', insertErr);
                          try { showError && showError('保存卡片到伺服器失敗，已在本地新增'); } catch (e) {}
                        }
                      } catch (err) {
                        console.warn('[ItineraryEditor] persist new card entry unexpected error', err);
                        try { showError && showError('保存卡片到伺服器失敗，已在本地新增'); } catch (e) {}
                      }
                    }
                  } catch (e) {
                    console.warn('[ItineraryEditor] persistence attempt failed', e);
                  }

                  // Insert into local itinerary (server id may have been applied above)
                  try {
                    if (placeholderCardInfo && typeof placeholderCardInfo.dayIndex === 'number' && typeof placeholderCardInfo.itemIndex === 'number' && placeholderCardInfo.dayIndex === dayIndex) {
                      const phIdx = placeholderCardInfo.itemIndex;
                      const existing = it.days[dayIndex].items || [];
                      if (phIdx >= 0 && phIdx < existing.length) {
                        it.days[dayIndex].items = [...existing.slice(0, phIdx), item, ...existing.slice(phIdx + 1)];
                      } else {
                        it.days[dayIndex].items = [...existing, item];
                      }
                      // clear placeholder marker after replacement
                      setPlaceholderCardInfo(null);
                    } else {
                      it.days[dayIndex].items = [...(it.days[dayIndex].items || []), item];
                    }
                  } catch (e) {
                    it.days[dayIndex].items = [...(it.days[dayIndex].items || []), item];
                  }

                  editor.setItinerary(it);
                  setDirty(true);

                  setTimePickerVisible(false);
                  setPendingCard(null);
                }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#FB7A4A',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 16,
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                確認
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline time overlay removed. */}
      {/* Save button fixed above FAB */}
      {/* hide SaveFab while quick-add menu is open to match requested behavior */}
  {/* Hide global SaveFab while an inline editor is open to avoid duplicate save affordances */}
  {/* Hide global SaveFab while any editor modal or inline editor is open to avoid duplicate save affordances */}
  {/* Hide SaveFab whenever we're not on the main timeline view or any editor/modal is open */}
  <SaveFab visible={canEdit && dirty && !quickAddOpen && currentSubView === 'timeline' && !inlineEditTarget && !(openPoi && openPoi.visible) && !openCustomEditor} saving={saving} onPress={saveItinerary} bottomOffset={fabBottomOffset} />
      </View>
      </View>

      {/* CardPicker bottom-sheet overlay: render on top of timeline when selecting a card */}
      {currentSubView === 'select-card' && (
        <div style={{ position: 'fixed', left: 0, right: 0, top: 0, bottom: 0, zIndex: 500, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          {/* Hide modal wrapper scrollbar to avoid double scrollbars on desktop */}
          <style>{`
            .cm-sheet-scroll { -ms-overflow-style: none; scrollbar-width: none; }
            .cm-sheet-scroll::-webkit-scrollbar { display: none; }
          `}</style>
          {/* backdrop (under the sheet) */}
          <div onClick={() => { setCurrentSubView('timeline'); setSheetExpanded(false); }} style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 501 }} />

          {/* sheet - ensure zIndex > backdrop so handle is visible above the dim */}
          {/* initial height 90vh, toggle to full 100vh via handle; add safe bottom padding inside so content isn't hidden by bottom nav */}
          <div style={{ width: '100%', maxWidth: isNarrow ? '100%' : 600, margin: '0 auto', height: sheetExpanded ? '100vh' : '90vh', maxHeight: '100vh', backgroundColor: '#fff', borderTopLeftRadius: sheetExpanded ? 0 : 12, borderTopRightRadius: sheetExpanded ? 0 : 12, overflow: 'hidden', boxShadow: '0 -8px 24px rgba(0,0,0,0.2)', zIndex: 502, transition: 'height 220ms ease' }}>
            {/* smaller header so the handle doesn't consume too much vertical space */}
            <div style={{ height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', top: 2 }}>
              {/* handle: capture pointer/touch events so dragging toggles expansion instead of scrolling inner content */}
              <div
                onPointerDown={handleSheetPointerDown}
                onTouchStart={handleSheetPointerDown}
                onClick={() => setSheetExpanded(prev => !prev)}
                style={{
                  width: 40,
                  height: 6,
                  background: '#e6e6e6',
                  borderRadius: 4,
                  cursor: 'grab',
                  zIndex: 503,
                  touchAction: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  display: 'block',
                }}
              />

              {/* close X when fully expanded so user can dismiss the full-screen sheet */}
              {sheetExpanded ? (
                <button onClick={() => { setCurrentSubView('timeline'); setSheetExpanded(false); }} style={{ position: 'absolute', right: 12, top: 8, zIndex: 504, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20 }} aria-label="关闭">
                  ✕
                </button>
              ) : null}
            </div>
            {/* content wrapper ensures inner content has bottom padding to avoid being obscured by bottom nav */}
            <div ref={cardPickerScrollRef} className="cm-sheet-scroll" style={{ height: 'calc(100% - 36px)', overflow: 'auto', paddingBottom: sheetBottomPadding, overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' as any }}>
              {/* 使用 CardListPage 作为选择器（列表模式，嵌入，右侧扁平加入按钮） */}
              <div style={{ maxWidth: 720, margin: '0 auto' }}>
                <CardList
                  pickerMode
                  title="从卡片库选择"
                  bottomInset={sheetBottomPaddingNumber + 48}
                  selectedTypes={placeholderCardInfo ? [placeholderCardInfo.cardType] : []}
                  onPageChange={scrollCardPickerToTop}
                  onSelectCard={async (card: any) => {
                    try {
                      setPendingCard(card);

                      const detectType = (c: any) => {
                        const candidates = [c?.card_type, c?.type, c?.base_payload?.card_type, c?.payload?.card_type];
                        for (const v of candidates) {
                          if (!v) continue;
                          try {
                            const s = String(v).toLowerCase().trim();
                            if (s === 'restaurant' || s === 'meal') return 'restaurant';
                            if (s === 'transport') return 'transport';
                            if (s === 'lodging' || s === 'hotel' || s === 'accommodation') return 'lodging';
                            if (s === 'shopping') return 'shopping';
                            if (s === 'activity') return 'activity';
                            if (s === 'poi') return 'poi';
                          } catch (e) {}
                        }
                        return null;
                      };

                      const guessed = detectType(card) || 'poi';
                      setCreateForm((prev: any) => ({ ...(prev || {}), type: guessed }));

                      // Try to fetch full row if available
                      try {
                        if (supabaseClient && card && card.id) {
                          const { data: full, error } = await supabaseClient.from('cards_cards').select('*').eq('id', card.id).maybeSingle();
                          if (!error && full) {
                            setPendingCard(full);
                            const guessed2 = detectType(full);
                            if (guessed2) setCreateForm((prev: any) => ({ ...(prev || {}), type: guessed2 }));
                          }
                        }
                      } catch (e) {
                        console.warn('[ItineraryEditor] fetch full card failed', e);
                      }

                      // Prefill time from placeholder if present
                      try {
                        if (placeholderCardInfo && typeof placeholderCardInfo.dayIndex === 'number' && typeof placeholderCardInfo.itemIndex === 'number') {
                          const phDay = placeholderCardInfo.dayIndex;
                          const phIdx = placeholderCardInfo.itemIndex;
                          const phItem = (editor.itinerary?.days?.[phDay]?.items || [])[phIdx];
                          if (phItem && phItem.time) {
                            setCreateForm((prev: any) => ({ ...(prev || {}), time: phItem.time }));
                          }
                        }
                      } catch (e) {}

                      setTimePickerVisible(true);
                      setCurrentSubView('timeline');
                    } catch (e) {
                      console.error('[ItineraryEditor] onSelectCard error', e);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 今日路线 底部 sheet - 抽屉式拖拽 */}
      {showRouteMap && (
        <div style={{ position: 'fixed', left: 0, right: 0, top: 0, bottom: 0, zIndex: 500, display: 'flex', alignItems: 'flex-end', pointerEvents: 'auto' }}>
          {/* backdrop */}
          <div onClick={() => { closeRouteMap(); }} style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.45)', pointerEvents: 'auto' }} />

          {/* drawer sheet */}
          <div 
            style={{ 
              width: '100%', 
              height: `${sheetHeight}vh`, 
              backgroundColor: '#fff', 
              borderTopLeftRadius: 12, 
              borderTopRightRadius: 12, 
              overflow: 'hidden', 
              boxShadow: '0 -8px 24px rgba(0,0,0,0.2)', 
              zIndex: 501,
              transition: isDragging ? 'none' : 'height 0.3s ease, border-radius 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto'
            }}
          >
            {/* 拖拽手柄区域 */}
            <div 
              onPointerDown={handleSheetPointerDown}
              onTouchStart={handleSheetPointerDown}
              style={{ 
                height: 48, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                flexShrink: 0,
                borderBottom: '1px solid #eee'
              }}
            >
              <div style={{ width: 40, height: 5, background: '#d1d5db', borderRadius: 3 }} />
              {/* 关闭按钮 (当接近全屏时显示) */}
              {sheetHeight > 80 && (
                <button 
                  onClick={() => { closeRouteMap(); }} 
                  style={{ position: 'absolute', right: 16, background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6B7280' }} 
                  aria-label="关闭"
                >
                  ✕
                </button>
              )}
            </div>
            {/* 统一滚动内容区：地图 + 列表 */}
            <div style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' as any, overscrollBehavior: 'contain' }}>
              <RouteMapSheet
                visible={showRouteMap}
                selectedDay={selectedDay}
                routeSpots={routeSpots}
                routeSpotsLoading={routeSpotsLoading}
                sheetHeight={sheetHeight}
                sheetBottomPadding={sheetBottomPaddingNumber}
                itineraryName={initialItinerary?.name || ''}
                onSelectSpot={(spotId: string) => {
                  try {
                    const foundIndex = (selectedDay?.items || []).findIndex((it: any) => String(it.id) === String(spotId));
                    if (foundIndex >= 0) {
                      const item = (selectedDay?.items || [])[foundIndex];
                      const modalPayload = item?.custom_payload || item?.inline_payload || item?.payload || {};
                      const finalPayload = { ...modalPayload, title: item?.title || modalPayload.title, _item: item };
                      setOpenPoi({ visible: true, payload: finalPayload, dayIndex: editor.selectedDayIndex, itemIndex: foundIndex });
                    } else {
                      try { navigate(`/card/${spotId}`); } catch (e) { window.location.pathname = `/card/${spotId}`; }
                    }
                  } catch (e) { console.error('onSelectSpot error', e); }
                }}
                onSelectListItem={(index: number) => {
                  try {
                    setOpenPoi({ visible: true, payload: { title: selectedDay?.items?.[index]?.title }, dayIndex: editor.selectedDayIndex, itemIndex: index });
                  } catch (e) {
                    console.error('onSelectListItem error', e);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* 懸浮式按鈕組 - 右下直列 icon-only（僅在非編輯模式顯示） */}
      {floatingButtonsVisible && (
        <div style={{
          position: 'fixed',
          right: 16,
          bottom: fabButtonsBottom,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          alignItems: 'flex-end',
          zIndex: 1000,
        }}>
          <button
            type="button"
            aria-label="地圖上顯示"
            onClick={() => setShowRouteMap(true)}
            style={{
              width: 48,
              height: 48,
              borderRadius: 999,
              border: 'none',
              background: '#FB7A4A',
              color: '#fff',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(251, 122, 74, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.5 3L20.34 3.03L15 5.1L9 3L3.36 4.9C3.15 4.97 3 5.15 3 5.38V20.5C3 20.78 3.22 21 3.5 21L3.66 20.97L9 18.9L15 21L20.64 19.1C20.85 19.03 21 18.85 21 18.62V3.5C21 3.22 20.78 3 20.5 3ZM15 19L9 16.89V5L15 7.11V19Z" fill="white"/>
            </svg>
          </button>

          {(isOwner || isCollaborator) && (
          <button
            type="button"
            aria-label="發佈為遊記"
            onClick={() => {
              try {
                launchPreview({ 
                  itinerary: editor.itinerary, 
                  title: resolvedTitle || undefined, 
                  city: resolvedCity || undefined, 
                  coverImage: resolvedCoverImage || undefined, 
                  sourceItineraryId: groupIdForLocal || undefined,
                  currentUser: reduxUser ? {
                    id: reduxUser.id,
                    display_name: reduxUser.display_name || reduxUser.user_metadata?.display_name,
                    avatar_url: reduxUser.avatar_url || reduxUser.user_metadata?.avatar_url,
                  } : undefined,
                });
              } catch (err) {
                console.error('Failed to launch preview', err);
                alert('無法建立預覽，請稍後再試');
              }
            }}
            style={{
              width: 48,
              height: 48,
              borderRadius: 999,
              border: 'none',
              background: '#FB7A4A',
              color: '#fff',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(251, 122, 74, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* send / publish icon (matches publish page affordance) */}
              <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" fill="white"/>
            </svg>
          </button>
          )}
        </div>
      )}

      <ItinerarySettingsModal
        visible={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        initialData={{
          title: resolvedTitle,
          coverUrl: resolvedCoverImage,
          startDate: initialItinerary?.start_date || initialFormData?.start_date,
          currency: initialItinerary?.currency || initialFormData?.currency,
          location: resolvedSubtitle,
        }}
        onSave={handleSaveSettings}
      />

      {/* Image Lightbox Portal - 使用与 SnapshotContentRenderer 相同的实现 */}
      {lightboxState && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setLightboxState(null)}
        >
          <img
            src={lightboxState.images[lightboxState.currentIndex].url}
            alt={lightboxState.images[lightboxState.currentIndex].alt || ''}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* 關閉按鈕 */}
          <button
            onClick={() => setLightboxState(null)}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#000',
              fontSize: 20,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            <MdClose size={24} />
          </button>
          
          {lightboxState.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxState({
                    ...lightboxState,
                    currentIndex: (lightboxState.currentIndex - 1 + lightboxState.images.length) % lightboxState.images.length
                  });
                }}
                style={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#333',
                  fontSize: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <MdChevronLeft size={32} color="#333" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxState({
                    ...lightboxState,
                    currentIndex: (lightboxState.currentIndex + 1) % lightboxState.images.length
                  });
                }}
                style={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#333',
                  fontSize: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <MdChevronRight size={32} color="#333" />
              </button>
              
              <div style={{
                position: 'absolute',
                bottom: 100,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: 16,
                fontSize: 14,
                fontWeight: 500,
              }}>
                {lightboxState.currentIndex + 1} / {lightboxState.images.length}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </View>
  );
};

export default ItineraryEditor;
